import { useState } from "react";

const US_BANNERS = [
  { id: "electronics", title: "Electronics", category: "Electronics", art: "/banners/banner-electronics.jpg" },
  { id: "home", title: "Home", category: "Home", art: "/banners/banner-home.jpg" },
  { id: "fashion", title: "Fashion", category: "Dresses", art: "/banners/banner-fashion.jpg" },
  { id: "beauty", title: "Beauty", category: "Beauty", art: "/banners/banner-beauty.jpg" },
];

const TONES = {
  electronics: "from-blue-600 via-indigo-500 to-violet-600",
  home: "from-orange-500 via-amber-400 to-rose-500",
  fashion: "from-fuchsia-600 via-pink-500 to-orange-400",
  dresses: "from-fuchsia-600 via-pink-500 to-orange-400",
  beauty: "from-rose-500 via-pink-400 to-amber-300",
  women: "from-fuchsia-600 via-rose-500 to-orange-400",
  men: "from-sky-600 via-blue-500 to-cyan-400",
  kids: "from-amber-400 via-orange-400 to-rose-400",
  footwear: "from-emerald-500 via-teal-400 to-cyan-400",
  health: "from-teal-500 via-emerald-400 to-lime-400",
  grocery: "from-lime-500 via-green-400 to-emerald-500",
};

export function marketBanners() {
  return US_BANNERS;
}

function BannerCard({ banner, onOpen, lead = false }) {
  const tone = TONES[banner.id] || TONES[banner.department] || "from-fuchsia-600 via-pink-500 to-orange-400";
  return (
    <button
      type="button"
      onClick={() => onOpen?.(banner)}
      data-testid={lead ? "shop-banner-lead" : `shop-banner-${banner.id}`}
      className={`relative h-36 w-full overflow-hidden rounded-2xl text-left text-white sm:h-44 bg-gradient-to-br ${tone}`}
    >
      {banner.art ? (
        <img src={banner.art} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : null}
      {banner.image && !banner.art ? (
        <img src={banner.image} alt="" className="absolute right-0 top-0 h-full w-1/2 object-contain p-2" referrerPolicy="no-referrer" />
      ) : null}
      <span className="absolute inset-y-0 left-0 flex w-[58%] flex-col justify-end bg-gradient-to-r from-black/55 to-transparent p-4">
        <span className="font-serif text-xl leading-tight sm:text-2xl">{banner.title}</span>
      </span>
    </button>
  );
}

export function ShopBanners({ banners, onOpen }) {
  const row = (banners || []).slice(0, 4);
  if (!row.length) return null;
  return (
    <section className="mb-8" data-testid="shop-banners" aria-label="Store banners">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="shop-banner-list">
        {row.map((banner, index) => (
          <BannerCard key={banner.id} banner={banner} onOpen={onOpen} lead={index === 0} />
        ))}
      </div>
    </section>
  );
}

function NavButton({ active, onClick, testId, children, child = false, pressed = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={pressed ? "true" : "false"}
      className={`flex min-w-0 flex-1 items-center justify-between text-left ${
        child ? "py-1.5 pl-3 text-[13px]" : "py-2 text-sm"
      } ${active ? "text-aura-accent font-medium" : "text-aura-ink/75 hover:text-aura-ink"}`}
    >
      {children}
    </button>
  );
}

export function ShopSidebar({
  countries,
  country,
  onCountry,
  sections,
  activeId,
  activeChild,
  activeGrand,
  onSelect,
  onSelectChild,
  onSelectGrand,
  stores,
  activeStore,
  onSelectStore,
}) {
  const code = (country || "").toUpperCase();
  const [collapsed, setCollapsed] = useState({});
  const visible = (sections || []).filter((section) => section.id === "all" || section.count !== 0);
  const toggle = (id) => setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  return (
    <aside
      className="mb-8 w-full shrink-0 border border-white/10 bg-white/[0.03] p-4 md:sticky md:top-24 md:mb-0 md:max-h-[calc(100vh-6.5rem)] md:w-60 md:overflow-y-auto"
      data-testid="shop-sidebar"
    >
      <label className="mb-2 block text-[11px] tracking-luxe uppercase text-aura-muted" htmlFor="shop-country">Country</label>
      <select
        id="shop-country"
        data-testid="shop-region"
        aria-label="Store region"
        value={code}
        onChange={(event) => onCountry?.(event.target.value)}
        className="mb-6 w-full border border-white/15 bg-black px-3 py-2 text-sm text-white"
      >
        {(countries || []).map((row) => (
          <option key={row.code} value={row.code}>{row.name}</option>
        ))}
      </select>
      <div className="mb-2 text-[11px] tracking-luxe uppercase text-aura-muted">Categories</div>
      <nav aria-label="Shop categories" data-testid="shop-categories">
        {visible.map((section) => {
          const open = !collapsed[section.id];
          const children = section.children || [];
          return (
            <div key={section.id} className="border-b border-white/5">
              <div className="flex items-start">
                {children.length > 0 ? (
                  <button
                    type="button"
                    aria-expanded={open ? "true" : "false"}
                    aria-label={`Toggle ${section.label}`}
                    data-testid={`shop-toggle-${section.id}`}
                    onClick={() => toggle(section.id)}
                    className="mt-2 w-4 shrink-0 text-xs text-aura-muted"
                  >
                    {open ? "▾" : "▸"}
                  </button>
                ) : <span className="w-4 shrink-0" />}
                <NavButton
                  active={activeId === section.id && !activeChild}
                  onClick={() => onSelect?.(section.id)}
                  testId={`shop-cat-${section.id}`}
                >
                  <span className="min-w-0 flex-1">{section.label}</span>
                </NavButton>
              </div>
              {open && children.length > 0 ? (
                <div className="mb-2 ml-2 border-l border-white/10">
                  {children.map((child) => {
                    const nested = child.children || [];
                    const childOpen = !collapsed[`${section.id}:${child.id}`];
                    return (
                      <div key={child.id}>
                        <div className="flex items-start">
                          {nested.length > 0 ? (
                            <button
                              type="button"
                              aria-expanded={childOpen ? "true" : "false"}
                              aria-label={`Toggle ${child.label}`}
                              onClick={() => toggle(`${section.id}:${child.id}`)}
                              className="mt-1.5 w-4 shrink-0 text-xs text-aura-muted"
                            >
                              {childOpen ? "▾" : "▸"}
                            </button>
                          ) : null}
                          <NavButton
                            child
                            active={activeChild === child.id && !activeGrand}
                            onClick={() => onSelectChild?.(section.id, child.id)}
                            testId={`shop-sub-${section.id}-${child.id}`}
                          >
                            <span className="min-w-0 flex-1">{child.label}</span>
                          </NavButton>
                        </div>
                        {childOpen && nested.length > 0 ? (
                          <div className="ml-4 border-l border-white/10">
                            {nested.map((grand) => (
                              <NavButton
                                key={grand.id}
                                child
                                active={activeGrand === grand.id && activeChild === child.id}
                                onClick={() => onSelectGrand?.(section.id, child.id, grand.id)}
                                testId={`shop-leaf-${section.id}-${child.id}-${grand.id}`}
                              >
                                <span className="min-w-0 flex-1">{grand.label}</span>
                              </NavButton>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
      {(stores || []).length > 0 ? (
        <>
          <div className="mb-2 mt-6 text-[11px] tracking-luxe uppercase text-aura-muted">Stores</div>
          <nav aria-label="Stores" data-testid="shop-stores">
            {stores.map((store) => (
              <NavButton
                key={store.id}
                active={activeStore === store.id}
                onClick={() => onSelectStore?.(store.id)}
                testId={`shop-store-${store.id}`}
              >
                <span className="min-w-0 flex-1">{store.label}</span>
              </NavButton>
            ))}
          </nav>
        </>
      ) : null}
    </aside>
  );
}
